/**
 * Quick-add, MOBILE — the first and only mobile variant in MeshHub, and a
 * REDUCTION of `QuickAdd.web.jsx` rather than a fork.
 *
 * WHY THIS ONE FLOW AND NOT THE APP. Exhibitions happen on phones. A rep at a
 * congress has a doctor in front of them and thirty seconds, and asking them
 * to find a laptop is asking them not to record the contact. Everything else —
 * the calendar, campaigns, the dashboard — is desk work that a phone would
 * make worse, so on mobile those routes render the desktop layout unchanged
 * and the user pinches to zoom, which is honest.
 *
 * WHAT "REDUCTION" MEANS HERE, concretely:
 *
 *   * the payload comes from `useQuickAdd`, the SAME hook the desktop dialog
 *     uses. `tests/mobile.test.jsx` asserts a narrow viewport submits exactly
 *     what the desktop one does;
 *   * it is a full-screen sheet rather than a centred dialog, because a modal
 *     with a backdrop on a phone-width screen leaves a form barely wider than
 *     its own padding;
 *   * the field-of-work select and the channel checkboxes are BELOW the fold,
 *     under a disclosure. Surname and phone are what a rep types while talking;
 *   * touch targets use the token control height, which is already large
 *     enough — nothing new was invented for mobile.
 *
 * Nothing is REMOVED that the desktop form can do. A reduction that dropped a
 * field would make the two produce different contacts, which is the failure
 * this file's whole structure exists to prevent.
 */
import { Button } from '@meshhub/design-tokens/ui';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import React, { useState } from 'react';
import { S } from '../lib/strings.js';
import { useQuickAdd } from '../lib/useQuickAdd.js';
import { ContactCrmFields, LookupField } from './contact/ContactCrmFields.web.jsx';
import { CheckboxField, MutationError, SelectField, TextArea, TextField }
  from './FormKit.web.jsx';

export function QuickAddSheet({ open, onOpenChange, onCreated }) {
  const [showMore, setShowMore] = useState(false);
  const {
    form,
    set,
    toggleChannel,
    submit,
    error,
    justAdded,
    creating,
    channels,
    facilityTypes,
    defaultType,
    lastNameRef,
  } = useQuickAdd({ open, onCreated, onDone: () => onOpenChange(false) });

  if (!open) return null;

  return (
    <div
      dir="rtl"
      role="dialog"
      aria-modal="true"
      aria-label={S.quickAdd.title}
      // FULL SCREEN. A centred modal on a phone leaves a form barely wider
      // than the backdrop's own padding.
      className="fixed inset-0 z-50 flex flex-col bg-bg"
    >
      <header className="flex items-center gap-2 border-b border-border p-3">
        <h1 className="flex-1 text-base font-bold text-text">{S.quickAdd.title}</h1>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label={S.app.cancel}
          className="h-control w-control rounded text-text-muted"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </header>

      <form
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit({ keepOpen: false });
        }}
      >
        <MutationError error={error} />
        {justAdded && (
          <p role="status" className="rounded border border-success px-3 py-2 text-sm text-success">
            {S.quickAdd.added(justAdded.displayName)}
          </p>
        )}

        {/* Surname first, and it is the only required field — what a rep has
            after ten seconds of conversation. */}
        <TextField
          ref={lastNameRef}
          label={S.quickAdd.lastName}
          value={form.lastName}
          onChange={(e) => set({ lastName: e.target.value })}
          required
          autoFocus
        />
        <TextField
          label={S.quickAdd.firstName}
          value={form.firstName}
          onChange={(e) => set({ firstName: e.target.value })}
        />
        <TextField
          label={S.quickAdd.phone}
          value={form.phone}
          onChange={(e) => set({ phone: e.target.value })}
          // `tel` brings up the numeric keypad. `dir=ltr` because an RTL
          // context reorders a phone number, which is the same rule the
          // desktop form follows (CLAUDE.md §12).
          type="tel"
          inputMode="tel"
          dir="ltr"
        />
        <TextArea
          label={S.quickAdd.note}
          hint={defaultType ? S.quickAdd.noteBecomesInteraction : undefined}
          value={form.note}
          onChange={(e) => set({ note: e.target.value })}
          rows={3}
        />

        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          aria-expanded={showMore}
          className="flex items-center gap-2 text-start text-sm text-text-muted"
        >
          {showMore ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
          {S.quickAdd.more}
        </button>

        {showMore && (
          <>
            {/* The SAME picker the desktop dialog renders — a reduction may
                reorder and hide behind a disclosure, never fork the control. */}
            <LookupField
              kind="FIELD_OF_WORK"
              label={S.quickAdd.fieldOfWork}
              value={form.fieldOfWorkId}
              onChange={(id) => set({ fieldOfWorkId: id })}
            />
            <fieldset className="flex flex-wrap gap-3">
              <legend className="pb-1 text-sm text-text-muted">{S.quickAdd.channels}</legend>
              {channels.map((c) => (
                <CheckboxField
                  key={c.id}
                  label={c.labelFa}
                  checked={form.channels.includes(c.code)}
                  onChange={() => toggleChannel(c.code)}
                />
              ))}
            </fieldset>
            <LookupField
              kind="PRODUCT"
              label={S.quickAdd.product}
              value={form.productId}
              onChange={(id) => set({ productId: id })}
            />
            {/* The SAME component the desktop dialog renders, one disclosure
                deeper. A reduction may reorder and may hide behind a
                disclosure; it may NOT drop a field, or the two variants start
                producing different contacts. */}
            <ContactCrmFields
              values={form}
              onChange={set}
              facilityTypes={facilityTypes}
            />
          </>
        )}
      </form>

      {/* The actions are PINNED. On a phone the form scrolls, and a submit
          button that scrolls away is a submit button people cannot find. */}
      <footer className="flex gap-2 border-t border-border p-3">
        <Button
          variant="primary"
          className="flex-1"
          disabled={creating}
          onClick={() => submit({ keepOpen: false })}
        >
          {S.quickAdd.submit}
        </Button>
        <Button
          className="flex-1"
          disabled={creating}
          onClick={() => submit({ keepOpen: true })}
        >
          {S.quickAdd.submitAndAnother}
        </Button>
      </footer>
    </div>
  );
}
